import type {Identity} from '../types';

/** hideInSimple: a setup/approval screen the backend handles itself in Simple mode (menu and route both hidden). */
export type NavItem={label:string;to?:string;permission?:string;requiresMultiBranch?:boolean;hideInSimple?:boolean};
export type NavGroup={category?:string;name:string;platform?:boolean;items:NavItem[]};

export const menuGroups:NavGroup[]=[
 {category:'HOME',name:'Overview',items:[
  {label:'Dashboard',to:'/dashboard'},
 ]},
 {category:'ACCOUNTS',name:'Accounts',items:[
  {label:'Chart of Accounts',to:'/accounts/chart',permission:'accounts.read'},
  {label:'Cash & Bank Vouchers',to:'/accounts/vouchers',permission:'vouchers.read'},
  {label:'Opening Balances',to:'/accounts/opening-balances',permission:'defaults.read'},
  {label:'Opening Stock Authorization',to:'/accounts/opening-stock-authorization',permission:'defaults.read'},
  {label:'Advances & Refunds',to:'/accounts/advances',permission:'party.advance.read'},
  {label:'Tax Codes & Profiles',to:'/accounts/tax-setup',permission:'tax.code.read',hideInSimple:true},
 ]},
 {name:'Financial Reports',items:[
  {label:'Account Statement',to:'/accounts/reports/statement',permission:'vouchers.read'},
  {label:'Trial Balance',to:'/accounts/reports/trial-balance',permission:'vouchers.read'},
  {label:'Profit & Loss',to:'/accounts/reports/profit-and-loss',permission:'vouchers.read'},
  {label:'Balance Sheet',to:'/accounts/reports/balance-sheet',permission:'vouchers.read'},
  {label:'Voucher Register',to:'/accounts/reports/voucher-register',permission:'vouchers.read'},
  {label:'Cash Book',to:'/accounts/reports/cash-book',permission:'vouchers.read'},
  {label:'Bank Book',to:'/accounts/reports/bank-book',permission:'vouchers.read'},
  {label:'Tax Register',to:'/accounts/tax-register',permission:'tax.code.read',hideInSimple:true},
  {label:'All Reports',to:'/accounts/all-reports',permission:'vouchers.read'},
 ]},
 {category:'GREY CONVERSION',name:'Masters',items:[
  {label:'Yarn Specifications',to:'/grey/yarn-specifications',permission:'grey.yarn.read'},
  {label:'Quality & Formula',to:'/grey/quality-formula',permission:'grey.quality.read'},
  {label:'Stock Locations',to:'/grey/stock-locations',permission:'grey.location.read'},
  {label:'Accounting Defaults',to:'/grey/accounting-defaults',permission:'defaults.read',hideInSimple:true},
 ]},
 {name:'Transactions',items:[
  {label:'Contract',to:'/grey/contracts',permission:'grey.contract.read'},
  {label:'Bag Purchase',to:'/grey/bag-purchases',permission:'grey.purchase.read'},
  {label:'Purchase Return',to:'/grey/purchase-returns',permission:'grey.purchase.read'},
  {label:'Mill Issue',to:'/grey/mill-issues',permission:'grey.issue.read'},
  {label:'Mill Yarn Return',to:'/grey/mill-yarn-returns',permission:'grey.millreturn.read'},
  {label:'Open Bag',to:'/grey/opened-bags',permission:'grey.issue.read'},
  {label:'Mill Reconciliation',to:'/grey/mill-reconciliations',permission:'grey.recon.read'},
  {label:'Fabric Receipt',to:'/grey/fabric-receipts',permission:'grey.receipt.read'},
  {label:'Fabric Cost Analysis',to:'/grey/fabric-cost-analyses',permission:'grey.costing.read'},
  {label:'Mill Bill',to:'/grey/mill-bills',permission:'grey.millbill.read'},
  {label:'Mill Charges Review',to:'/grey/mill-charges-review',permission:'grey.millbill.read'},
  {label:'Mill Payment',to:'/grey/mill-payment-requests',permission:'grey.millpay.read'},
  {label:'Customer Contract',to:'/grey/customer-contracts',permission:'grey.sale.read'},
  {label:'Finished Sale',to:'/grey/fabric-sale-drafts',permission:'grey.sale.read'},
 ]},
 {name:'Reports',items:[
  {label:'Contract Position',to:'/grey/contract-position',permission:'grey.contract.read'},
  {label:'Finished Fabric Stock',to:'/grey/finished-fabric-stock',permission:'grey.receipt.read'},
  {label:'Sales Register',to:'/grey/sales-register',permission:'grey.sale.read'},
  {label:'Customer Contract Status',to:'/grey/reports/customer-contracts',permission:'grey.sale.read'},
  {label:'Mill Statement',to:'/grey/mill-statements',permission:'grey.millstmt.read'},
  {label:'Mill Bags Balance',to:'/grey/reports/mill-bags-balance',permission:'grey.report.millbags.read'},
  {label:'Fabric Receipt Report',to:'/grey/reports/fabric-receipt',permission:'grey.report.receipts.read'},
  {label:'Party Outstanding',to:'/grey/reports/outstanding',permission:'grey.report.outstanding.read'},
  {label:'Inventory Valuation',to:'/grey/reports/inventory-valuation',permission:'grey.report.valuation.read'},
  {label:'Production Yield',to:'/grey/reports/production-yield',permission:'grey.report.yield.read'},
 ]},
 {category:'SYSTEM',name:'Administration',items:[
  {label:'Branches',to:'/system/branches',permission:'admin.branch.read',requiresMultiBranch:true},
  {label:'Users & Roles',to:'/system/users',permission:'admin.user.read'},
  {label:'Roles',to:'/system/roles',permission:'admin.role.read'},
  {label:'Authorization',to:'/system/authorization',permission:'admin.user.read',hideInSimple:true},
  {label:'Admin Settings',to:'/system/settings',permission:'admin.settings.read'},
  {label:'Fiscal Year',to:'/system/fiscal-years',permission:'admin.fiscal.read',hideInSimple:true},
  {label:'Simple mode',to:'/system/simple-mode',permission:'accounts.posting_control.update'},
  {label:'Financial Activation',to:'/system/financial-activation',permission:'financial.activation.read',hideInSimple:true},
 ]},
 {name:'Platform Admin',platform:true,items:[
  {label:'Companies & Plans',to:'/system/companies'},
  {label:'Plans & Billing',to:'/system/plans'},
 ]},
];

function sessionCanSee(user:Identity,group:NavGroup,item:NavItem){
 if(item.requiresMultiBranch&&user.multiBranchEnabled!==true)return false;
 if(item.hideInSimple&&user.simpleMode===true)return false;
 if(item.permission&&!user.permissions.includes(item.permission))return false;
 if(!group.platform&&!user.modules.includes('accounts'))return false;
 return !!item.to;
}

/** Sidebar groups after platform-admin, multi-branch, and permission rules. */
export function visibleMenu(user:Identity):NavGroup[]{
 const rows:NavGroup[]=[];
 let pendingCaption:string|undefined;
 for(const group of menuGroups){
  if(group.platform&&user.isPlatformAdmin!==true)continue;
  if(group.category)pendingCaption=group.category;
  const items=group.items.filter(item=>sessionCanSee(user,group,item));
  if(!items.length)continue;
  rows.push({...group,items,category:pendingCaption});
  pendingCaption=undefined;
 }
 return rows;
}

export function routeAllowed(user:Identity,path:string){
 const match=menuGroups.flatMap(group=>group.items.map(item=>({group,item}))).find(row=>row.item.to===path);
 if(!match)return true;
 if(match.group.platform&&user.isPlatformAdmin!==true)return false;
 return sessionCanSee(user,match.group,match.item);
}
